import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { renderColumns, transformData } from './renderColumns';
import { Navigation } from '../../../utils/navigation';
import { useSearch } from '../../../hooks/useSearch';
import { usePagination } from '../../../hooks/usePagination';
import { redirectToPage } from '../../../utils/helpers';
import { BaseTable, Loader, Pagination } from '../../../components';
import { useExportRidesModal } from '../../../components/modals/ExportRidesModal/useExportRidesModal';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { fetchHOInvoices, downloadHOInvoicePDF, downloadHOInvoiceReceipts } from '../../../store/ho_billing';

import classNames from 'classnames';
import styles from './index.module.css';
import { InfoIcon } from '../../../assets';

export const HO_Invoices = ({
  hoInvoices,
  fetchHOInvoices,
  downloadHOInvoicePDF,
  downloadHOInvoiceReceipts,
  role,
  limit,
  currentPage,
  total,
  totalPages,
  loading,
}) => {
  const navigate = Navigation();
  const { searchValue, highlighted } = useSearch(fetchHOInvoices);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);

  usePagination(navigate);

  useEffect(() => {
    fetchHOInvoices({ filtered: false, Navigation: navigate });
    return () => {};
  }, [fetchHOInvoices]);

  const handleConfirmExport = useCallback(
    ({ fileType }, closeModal) => {
      downloadHOInvoiceReceipts({ invoiceId: selectedInvoiceId, fileType });
      closeModal();
    },
    [downloadHOInvoiceReceipts, selectedInvoiceId],
  );

  const { Modal: ExportModal, setExportModalVisible } = useExportRidesModal({
    onConfirm: handleConfirmExport,
  });

  const handleExportClick = useCallback(
    (invoiceId) => () => {
      setSelectedInvoiceId(invoiceId);
      setExportModalVisible(true);
    },
    [setExportModalVisible],
  );

  const handleDownloadInvoice = useCallback(
    (filePath) => () => {
      downloadHOInvoicePDF(filePath);
    },
    [],
  );

  const handleChangePage = (page) => {
    redirectToPage(navigate?.location?.pathname, page, navigate);
    fetchHOInvoices({ Navigation: navigate });
  };

  const tableData = useMemo(() => {
    return transformData({ data: hoInvoices, handleDownloadInvoice, handleExportClick });
  }, [hoInvoices, handleDownloadInvoice, handleExportClick]);

  const columns = useMemo(() => {
    return role ? renderColumns(searchValue, highlighted) : null;
  }, [role, hoInvoices, highlighted, searchValue]);

  const InvoiceTable = useMemo(
    () => <BaseTable data={tableData} columns={columns} addHoverEffectToRowTable />,
    [tableData, columns],
  );

  return (
    <div className={styles.pageContent}>
      <Loader loading={loading} />
      <div className={styles.infoBanner}>
        <InfoIcon />
        <span className={styles.infoBannerText}>Billing Tab Only Available to MedHaul Admins</span>
      </div>
      <div>{InvoiceTable}</div>
      <div className={styles.tableFooter}>
        <div
          className={classNames({
            [styles.pagination]: true,
            [styles.gapForPagination]: hoInvoices?.length > 4,
          })}
        >
          <Pagination
            defaultCurrent={currentPage}
            total={total}
            totalPages={totalPages}
            onChange={handleChangePage}
            pageSize={limit}
          />
        </div>
      </div>
      {ExportModal}
    </div>
  );
};

const mapStateToProps = (state) => ({
  hoInvoices: state.hoBilling.hoInvoices,
  limit: state.hoBilling.limit,
  currentPage: state.hoBilling.currentPage,
  total: state.hoBilling.total,
  totalPages: state.hoBilling.totalPages,
  loading: state.hoBilling.loading,
  role: state.authorization.role,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ fetchHOInvoices, downloadHOInvoicePDF, downloadHOInvoiceReceipts }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(HO_Invoices);
